// src/pages/auth/SignupPage.tsx
import React, { useState, useEffect } from 'react';
import { User, Mail, Smartphone, Lock, CheckCircle, Search, ArrowLeft } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faSpinner } from '@fortawesome/free-solid-svg-icons';

interface SignupPageProps {
  onShowLogin: () => void;
  initialUserType: 'buyer' | 'seller';
}

const SignupPage: React.FC<SignupPageProps> = ({ onShowLogin, initialUserType }) => {
  const [userType, setUserType] = useState(initialUserType);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Data States
  const [gstNo, setGstNo] = useState('');
  const [verifiedData, setVerifiedData] = useState<any>(null);
  
  const [formData, setFormData] = useState({ username: '', email: '', mobile: '', password: '', confirmPassword: '' });
  const [verificationCode, setVerificationCode] = useState('');
  const [showVerificationInput, setShowVerificationInput] = useState(false);

  useEffect(() => { setUserType(initialUserType); setStep(1); }, [initialUserType]);

  // --- LOGIC SAME AS BEFORE ---
  const handleVerifyGST = async () => {
    if(gstNo.length !== 15) { setMessage({type: 'error', text: 'Enter valid 15-digit GSTIN'}); return; }
    setLoading(true); setMessage(null);
    try {
        const res = await fetch(`/springboot/api/auth/verify-gst/${gstNo}`);
        const data = await res.json();
        if(res.ok) { setVerifiedData(data); setMessage(null); setStep(2); } else { setMessage({type: 'error', text: 'Invalid GST Number'}); }
    } catch(e) { setMessage({type: 'error', text: 'Verification Failed'}); } finally { setLoading(false); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) { setMessage({ type: 'error', text: "Passwords don't match" }); return; }
    setLoading(true);
    try {
        let apiUrl = `/springboot/api/${userType === 'buyer' ? 'buyers' : 'sellers'}/register`;
        let payload: any = { ...formData };
        if (userType === 'seller' && verifiedData) {
            payload.companyName = verifiedData.companyName; payload.gstNumber = gstNo; payload.addressLine1 = verifiedData.address;
            payload.city = verifiedData.city; payload.state = verifiedData.state; payload.postalCode = verifiedData.postalCode;
        }
        const res = await fetch(apiUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        if (res.ok) { setMessage({ type: 'success', text: 'Registered! Check email for OTP.' }); setShowVerificationInput(true); } else { setMessage({ type: 'error', text: await res.text() }); }
    } catch (error) { setMessage({ type: 'error', text: 'Network Error' }); } finally { setLoading(false); }
  };

  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault(); setLoading(true);
    try {
      const res = await fetch('/springboot/api/auth/verify-email', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: formData.email, token: verificationCode, userType }) });
      if (res.ok) { setMessage({ type: 'success', text: 'Verified! Redirecting...' }); setTimeout(onShowLogin, 1500); } else { setMessage({ type: 'error', text: 'Invalid Code' }); }
    } catch(e) { setMessage({ type: 'error', text: 'Error' }); } finally { setLoading(false); }
  };

  // --- STYLING CONSTANTS ---
  const inputClass = "w-full pl-11 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200 text-gray-700 text-sm font-medium";
  const iconClass = "absolute inset-y-0 left-0 flex items-center pl-3.5 text-gray-400 pointer-events-none group-focus-within:text-emerald-500 transition-colors";

  return (
    <div className="flex flex-col items-center justify-center h-full w-full px-6 animate-fade-in">
      <div className="w-full max-w-sm">
        
        {/* HEADER WITH BACK BUTTON */}
        <div className="flex items-center mb-6 relative">
            <button onClick={onShowLogin} className="absolute left-[-10px] p-2 text-gray-400 hover:text-gray-800 hover:bg-gray-100 rounded-full transition-colors">
                <ArrowLeft size={20}/>
            </button>
            <div className="w-full text-center">
                <h2 className="text-2xl font-extrabold text-gray-800">Join Us</h2>
                <p className="text-xs text-emerald-600 font-bold uppercase tracking-wider mt-0.5">
                    Create {userType} Account
                </p>
            </div>
        </div>

        {message && (
          <div className={`p-3 mb-4 rounded-xl text-center text-sm font-semibold shadow-sm ${message.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
            {message.text}
          </div>
        )}

        {/* --- STEP 1: GST VERIFICATION (SELLER ONLY) --- */}
        {userType === 'seller' && step === 1 && !showVerificationInput && (
            <div className="space-y-5 animate-fade-in">
                <div className="bg-emerald-50 p-6 rounded-2xl border border-emerald-100 text-center shadow-sm">
                    <p className="text-sm text-emerald-900 font-bold mb-3">GST Verification Required</p>
                    <div className="flex gap-2 mb-3">
                        <input 
                            type="text" 
                            className="flex-1 border-2 border-emerald-200 p-3 rounded-lg uppercase font-bold text-center tracking-widest focus:ring-2 focus:ring-emerald-500 outline-none text-emerald-900 placeholder:text-emerald-300"
                            placeholder="GSTIN NUMBER"
                            maxLength={15}
                            value={gstNo}
                            onChange={(e)=>setGstNo(e.target.value.toUpperCase())}
                        />
                    </div>
                    <button onClick={handleVerifyGST} disabled={loading} className="w-full bg-emerald-600 text-white py-2.5 rounded-lg hover:bg-emerald-700 shadow-md transition-all font-semibold flex justify-center items-center gap-2">
                        {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : <><Search size={16} /> Verify Business</>}
                    </button>
                </div>
                <div className="text-center">
                    <button onClick={onShowLogin} className="text-sm text-gray-500 hover:text-emerald-600 font-medium">Already have an account? Login</button>
                </div>
            </div>
        )}

        {/* --- STEP 2: REGISTRATION FORM --- */}
        {((userType === 'buyer') || (userType === 'seller' && step === 2)) && !showVerificationInput && (
          <form onSubmit={handleSubmit} className="space-y-4 animate-fade-in-up">
            
            {/* Verified Badge */}
            {userType === 'seller' && verifiedData && (
                <div className="bg-emerald-50 p-3 rounded-xl border border-emerald-200 flex items-start gap-3 mb-2 shadow-sm">
                    <FontAwesomeIcon icon={faCheckCircle} className="text-emerald-600 mt-1 text-lg" />
                    <div>
                        <p className="text-xs font-bold text-emerald-800 uppercase">Verified Business</p>
                        <p className="font-bold text-gray-900 text-sm">{verifiedData.companyName}</p>
                        <button type="button" onClick={()=>setStep(1)} className="text-[10px] text-red-500 font-bold hover:underline mt-1">CHANGE GST</button>
                    </div>
                </div>
            )}

            <div className="relative group">
              <span className={iconClass}><User size={18} /></span>
              <input type="text" placeholder="Full Name" value={formData.username} onChange={e=>setFormData({...formData, username:e.target.value})} className={inputClass} required />
            </div>

            <div className="relative group">
              <span className={iconClass}><Mail size={18} /></span>
              <input type="email" placeholder="Email Address" value={formData.email} onChange={e=>setFormData({...formData, email:e.target.value})} className={inputClass} required />
            </div>

            <div className="relative group">
              <span className={iconClass}><Smartphone size={18} /></span>
              <input type="text" placeholder="Mobile Number" maxLength={10} value={formData.mobile} onChange={e=>setFormData({...formData, mobile:e.target.value})} className={inputClass} required />
            </div>

            <div className="grid grid-cols-2 gap-3">
                <div className="relative group">
                    <span className={iconClass}><Lock size={18} /></span>
                    <input type="password" placeholder="Password" value={formData.password} onChange={e=>setFormData({...formData, password:e.target.value})} className={inputClass} required />
                </div>
                <div className="relative group">
                    <span className={iconClass}><Lock size={18} /></span>
                    <input type="password" placeholder="Confirm" value={formData.confirmPassword} onChange={e=>setFormData({...formData, confirmPassword:e.target.value})} className={inputClass} required />
                </div>
            </div>

            <button type="submit" disabled={loading} className="w-full bg-emerald-600 text-white font-bold py-3.5 rounded-xl shadow-lg shadow-emerald-200 hover:bg-emerald-700 hover:shadow-xl transition transform active:scale-[0.98] mt-2">
              {loading ? 'Creating Account...' : 'Complete Registration'}
            </button>
          </form>
        )}

        {/* --- STEP 3: OTP VERIFICATION --- */}
        {showVerificationInput && (
           <form onSubmit={handleVerifyOTP} className="space-y-6 animate-fade-in mt-4">
               <div className="text-center">
                    <div className="bg-emerald-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-emerald-600">
                        <Mail size={32} />
                    </div>
                    <h3 className="text-lg font-bold text-gray-800">Verify your Email</h3>
                    <p className="text-gray-500 text-sm mt-1">We sent a code to <span className="font-bold text-gray-700">{formData.email}</span></p>
               </div>
               
               <input 
                 type="text" 
                 placeholder="0 0 0 0 0 0" 
                 value={verificationCode} 
                 onChange={e=>setVerificationCode(e.target.value)} 
                 className="w-full py-4 border-2 border-gray-200 rounded-xl text-center text-3xl tracking-[0.5em] font-bold focus:ring-4 focus:ring-emerald-100 focus:border-emerald-500 outline-none transition-all text-emerald-800 placeholder:text-gray-200" 
                 maxLength={6}
                 required 
               />
               
               <button type="submit" disabled={loading} className="w-full bg-emerald-600 text-white font-bold py-3.5 rounded-xl shadow-lg hover:bg-emerald-700 transition-all">
                   {loading ? 'Verifying...' : 'Verify & Login'}
               </button>
           </form>
        )}

      </div>
    </div>
  );
};

export default SignupPage;